// src/components/plm/ActiveOrders.jsx
import styles from "../../styles/PLMApp.module.css";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

function toDateLabel(raw) {
  if (!raw) return "—";
  const d = raw?.toDate ? raw.toDate() : new Date(raw);
  if (!d || Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getBuyer(order) {
  return order?.Buyer?.name || "—";
}
function getBuyerLocation(order) {
  return order?.Buyer?.location || "—";
}
function getSupplier(order) {
  return order?.Suppliers?.[0]?.name || "—";
}
function getSupplierLocation(order) {
  return order?.Suppliers?.[0]?.location || "—";
}
function getConstruction(order) {
  return order?.fabricDefault || order?.construction || "—";
}
function getDelivery(order) {
  return order?.Suppliers?.[0]?.deliveryDate || order?.deliveryDate || null;
}

function getDaysLeft(order) {
  const raw = getDelivery(order);
  if (!raw) return null;
  const d = raw?.toDate ? raw.toDate() : new Date(raw);
  if (!d || Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

// ── Confirmation modal ───────────────────────────────────────────────────────
function ConfirmModal({ modal, onConfirm, onDismiss }) {
  if (!modal) return null;
  const isArchive = modal.action === "archive";

  return createPortal(
    <div className={styles.modalOverlay} onClick={onDismiss}>
      <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
        {/* Title */}
        <div>
          <div className={styles.modalTag}>
            {isArchive ? "Archive Order" : "Cancel Order"}
          </div>
          <div className={styles.modalTitle}>
            {modal.orderNumber || modal.orderId}
          </div>
        </div>

        {/* Divider */}
        <div className={styles.modalDivider} />

        {/* Warning */}
        <div className={styles.modalWarning}>
          {isArchive
            ? "This order will be marked as archived and its workflow steps and activity logs will be permanently deleted. Documents will be retained. This action cannot be undone."
            : "This order will be marked as cancelled and its workflow steps and activity logs will be permanently deleted. Documents will be retained. This action cannot be undone."}
        </div>

        {/* Buttons */}
        <div className={styles.modalActions}>
          <button
            type="button"
            onClick={onDismiss}
            className={styles.btnModalDismiss}
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={
              isArchive
                ? styles.btnModalConfirmArchive
                : styles.btnModalConfirmCancel
            }
          >
            Confirm {isArchive ? "Archive" : "Cancel"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ActiveOrders({
  orders = [],
  activeOrderId,
  onSelectOrder,
  onArchive,
  onCancel,
  isAdmin,
}) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [modal, setModal] = useState(null);

  const activeOrders = useMemo(
    () => orders.filter((order) => !order?.archived && !order?.cancelled),
    [orders],
  );

  const visibleOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return activeOrders
      .filter((order) => {
        if (filter === "Selected") return order.id === activeOrderId;
        if (filter === "Unselected") return order.id !== activeOrderId;
        return true;
      })
      .filter((order) => {
        if (!q) return true;
        return [
          order.orderNumber,
          order.id,
          getBuyer(order),
          getSupplier(order),
          getConstruction(order),
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));
      });
  }, [activeOrders, activeOrderId, filter, search]);

  const selectedOrder =
    activeOrders.find((o) => o.id === activeOrderId) || null;
  const selectedOrderNumber = selectedOrder?.orderNumber || "—";
  const selectedOrderBuyer = selectedOrder
    ? getBuyer(selectedOrder)
    : "No order selected";

  const [displayOrderNumber, setDisplayOrderNumber] =
    useState(selectedOrderNumber);
  const [displayOrderBuyer, setDisplayOrderBuyer] =
    useState(selectedOrderBuyer);
  const [orderCardVisible, setOrderCardVisible] = useState(true);

  useEffect(() => {
    setOrderCardVisible(false);
    const timeout = setTimeout(() => {
      setDisplayOrderNumber(selectedOrderNumber);
      setDisplayOrderBuyer(selectedOrderBuyer);
      setOrderCardVisible(true);
    }, 180);
    return () => clearTimeout(timeout);
  }, [selectedOrderNumber, selectedOrderBuyer]);

  const urgentCount = activeOrders.filter((order) => {
    const days = getDaysLeft(order);
    return days !== null && days <= 21;
  }).length;

  const toggleSelect = (orderId) => {
    onSelectOrder?.(orderId === activeOrderId ? null : orderId);
  };

  const handleModalConfirm = () => {
    if (!modal) return;
    if (modal.action === "archive") onArchive?.(modal.orderId);
    if (modal.action === "cancel") onCancel?.(modal.orderId);
    setModal(null);
  };

  // Dynamic grid column count passed as CSS variable — breakpoints can override freely
  const orderColsVar = isAdmin
    ? "200px 1fr 1fr 0.8fr 0.8fr 140px 130px"
    : "200px 1fr 1fr 0.8fr 0.8fr 160px";

  return (
    <>
      {/* ── Stat cards ──────────────────────────────────────────────────── */}
      <div className={styles.dashGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Active Orders</div>
          <div className={styles.statValue}>{activeOrders.length}</div>
          <div className={styles.statSub}>Current live order records</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Selected Order</div>
          <div
            className={styles.statValue}
            style={{
              fontSize: 34,
              minHeight: 40,
              transition:
                "opacity 0.42s cubic-bezier(0.16,1,0.3,1), transform 0.42s cubic-bezier(0.16,1,0.3,1)",
              opacity: orderCardVisible ? 1 : 0,
              transform: orderCardVisible
                ? "translateY(0)"
                : "translateY(10px)",
              willChange: "opacity, transform",
            }}
          >
            {displayOrderNumber}
          </div>
          <div
            className={styles.statSub}
            style={{
              minHeight: 18,
              transition:
                "opacity 0.42s cubic-bezier(0.16,1,0.3,1), transform 0.42s cubic-bezier(0.16,1,0.3,1)",
              opacity: orderCardVisible ? 1 : 0,
              transform: orderCardVisible ? "translateY(0)" : "translateY(8px)",
              willChange: "opacity, transform",
            }}
          >
            {displayOrderBuyer}
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Urgent Delivery</div>
          <div className={`${styles.statValue} ${styles.cellValueUrgent}`}>
            {urgentCount}
          </div>
          <div className={styles.statSub}>Orders within 21 days</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Admin Access</div>
          <div
            className={styles.statValue}
            style={{
              color: isAdmin ? "#6abf9a" : "#d07070",
              fontSize: 28,
            }}
          >
            {isAdmin ? "ON" : "OFF"}
          </div>
          <div className={styles.statSub}>
            {isAdmin ? "Archive & cancel controls enabled" : "View only"}
          </div>
        </div>
      </div>

      {/* ── Search + filter bar ─────────────────────────────────────────── */}
      <div className={styles.panel} style={{ marginBottom: 24 }}>
        <div className={styles.panelTitle}>Active Order Registry</div>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 280 }}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by order id, buyer, supplier, or construction"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className={styles.docTagFilter} style={{ marginBottom: 0 }}>
            {["All", "Selected", "Unselected"].map((item) => (
              <button
                key={item}
                type="button"
                className={`${styles.docTag} ${filter === item ? styles.docTagActive : ""}`}
                onClick={() => setFilter(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Order list ──────────────────────────────────────────────────── */}
      {visibleOrders.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>📦</div>
          <div className={styles.emptyStateText}>
            No active orders match the current search or filter.
          </div>
        </div>
      ) : (
        <div
          className={styles.dashPanels}
          style={{ gridTemplateColumns: "1fr" }}
        >
          {visibleOrders.map((order) => {
            const selected = order.id === activeOrderId;
            const daysLeft = getDaysLeft(order);

            return (
              <div
                key={order.id}
                className={`${styles.panel} ${styles.orderPanel} ${selected ? styles.orderPanelSelected : ""}`}
              >
                <div
                  className={styles.orderRow}
                  style={{ "--order-cols": orderColsVar }}
                >
                  {/* Order number ─────────────────────────────────────── */}
                  <div
                    className={`${styles.orderCellFirst} ${selected ? styles.orderCellFirstSelected : ""}`}
                  >
                    <div className={styles.cellLabel}>Order</div>
                    <div
                      className={`${styles.partyPulse2} ${styles.orderNumberDisplay} ${selected ? styles.orderNumberDisplaySelected : ""}`}
                    >
                      {order.orderNumber || "—"}
                    </div>
                    <div className={styles.orderIdDisplay}>{order.id}</div>
                  </div>

                  {/* Buyer ────────────────────────────────────────────── */}
                  <div className={styles.orderCell}>
                    <div className={styles.cellLabel}>Buyer</div>
                    <div className={styles.cellValue}>{getBuyer(order)}</div>
                    <div className={styles.cellMeta}>
                      {getBuyerLocation(order)}
                    </div>
                  </div>

                  {/* Supplier ────────────────────────────────────────── */}
                  <div className={styles.orderCell}>
                    <div className={styles.cellLabel}>Supplier</div>
                    <div className={styles.cellValue}>{getSupplier(order)}</div>
                    <div className={styles.cellMeta}>
                      {getSupplierLocation(order)}
                    </div>
                  </div>

                  {/* Construction ────────────────────────────────────── */}
                  <div className={styles.orderCell}>
                    <div className={styles.cellLabel}>Construction</div>
                    <div className={styles.cellValueConstruction}>
                      {getConstruction(order)}
                    </div>
                  </div>

                  {/* Delivery ────────────────────────────────────────── */}
                  <div className={styles.orderCell}>
                    <div className={styles.cellLabel}>Delivery</div>
                    <div
                      className={`${styles.cellValue} ${daysLeft !== null && daysLeft <= 21 ? styles.cellValueUrgent : ""}`}
                    >
                      {toDateLabel(getDelivery(order))}
                    </div>
                    <div className={styles.cellMeta}>
                      {daysLeft === null
                        ? "No delivery date"
                        : `${daysLeft} days remaining`}
                    </div>
                  </div>

                  {/* Select toggle ───────────────────────────────────── */}
                  <div
                    className={`${styles.orderCellSelect} ${selected ? styles.orderCellSelectOn : ""}`}
                  >
                    <div
                      className={`${styles.toggleLabel} ${selected ? styles.toggleLabelOn : ""}`}
                    >
                      {selected ? "Selected" : "Deselected"}
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={selected}
                      aria-label={
                        selected
                          ? `Deselect order ${order.orderNumber || order.id}`
                          : `Select order ${order.orderNumber || order.id}`
                      }
                      onClick={() => toggleSelect(order.id)}
                      className={`${styles.toggleTrack} ${selected ? styles.toggleTrackOn : ""}`}
                    >
                      <span
                        className={`${styles.toggleKnob} ${selected ? styles.toggleKnobOn : ""}`}
                      />
                    </button>
                  </div>

                  {/* Admin actions ───────────────────────────────────── */}
                  {isAdmin && (
                    <div className={styles.orderCellAdmin}>
                      <button
                        type="button"
                        className={styles.btnArchive}
                        onClick={() =>
                          setModal({
                            orderId: order.id,
                            orderNumber: order.orderNumber,
                            action: "archive",
                          })
                        }
                      >
                        Archive
                      </button>
                      <button
                        type="button"
                        className={styles.btnCancelOrder}
                        onClick={() =>
                          setModal({
                            orderId: order.id,
                            orderNumber: order.orderNumber,
                            action: "cancel",
                          })
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Confirmation modal ───────────────────────────────────────────── */}
      <ConfirmModal
        modal={modal}
        onConfirm={handleModalConfirm}
        onDismiss={() => setModal(null)}
      />
    </>
  );
}
